/**
 * useIosEdgeSwipeBack — iOS left-edge swipe-back gesture.
 *
 * WKWebView has no built-in back-forward swipe (and the native
 * `allowsBackForwardNavigationGestures` is unreliable in a History-API SPA —
 * documented blank swipe-back page — and gives no hook to coordinate our slide).
 * So on iOS we synthesize it in JS: a swipe that starts within ~24px of the left
 * edge and moves dominantly rightward past a threshold calls `navigate(-1)`,
 * which routes through App.js's View-Transitions effect and plays the reverse
 * (`vt-back`) slide automatically.
 *
 * iOS only. **Android is intentionally excluded**: its OS gesture-nav edge swipe
 * already pops `window.history` (Capacitor's back handling) — which now animates
 * via the same transition effect — so a JS gesture there would double-fire.
 *
 * No-op on web. Mount once (AppWithBottomNav).
 */
import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { TAB_ROOTS } from '../utils/navConstants';

const EDGE_PX = 44;    // arm only when the touch starts within this of the left edge.
                       // Wide enough to survive iOS edge-protection: iOS defers the
                       // first few px of an edge swipe to the system, so the WebView's
                       // touchstart often registers ~20-40px inward (24px was too narrow
                       // — the gesture never armed; 44px fixed it, verified on device).
const COMMIT_PX = 64;  // horizontal travel that triggers the back
const SLOP_PX = 8;     // movement before we lock in a direction

// Walk up from the touch target; bail if we're inside a horizontally-scrollable
// element (carousels, pill rows) so the gesture doesn't hijack their scroll.
function isInHorizontalScroller(el) {
  let node = el;
  while (node && node !== document.body && node.nodeType === 1) {
    const ox = window.getComputedStyle(node).overflowX;
    if ((ox === 'auto' || ox === 'scroll') && node.scrollWidth > node.clientWidth + 1) return true;
    node = node.parentElement;
  }
  return false;
}

export default function useIosEdgeSwipeBack() {
  const navigate = useNavigate();
  const location = useLocation();
  // Keep the current pathname in a ref so the listener effect's onStart can read
  // the live path WITHOUT adding `location` to that effect's deps — otherwise the
  // document touch listeners would tear down + re-subscribe on every navigation.
  const pathRef = useRef(location.pathname);
  useEffect(() => {
    pathRef.current = location.pathname;
  }, [location.pathname]);

  useEffect(() => {
    if (Capacitor?.getPlatform?.() !== 'ios') return undefined;

    let startX = 0, startY = 0, armed = false, decided = false, fired = false;

    const onStart = (e) => {
      armed = decided = fired = false;
      if (e.touches.length !== 1) return;
      // A ProductDetail vaul sheet is open → the drawer owns gestures (drag-down
      // to dismiss); don't also run an edge-swipe behind it. RR stores the route
      // state under history.state.usr.
      if (window.history.state?.usr?.backgroundLocation) return;
      const t = e.touches[0];
      if (t.clientX > EDGE_PX) return;
      if (isInHorizontalScroller(e.target)) return;
      // Tab-root suppression: tab roots have no back stack, so suppress the
      // gesture there — EXCEPT /cart when it was NOT reached via the bottom nav
      // (a non-tab caller, e.g. CartDrawer → Cart, gets a back/swipe-back).
      const path = pathRef.current;
      const fromTab = window.history.state?.usr?.fromTab === true;
      if (TAB_ROOTS.has(path) && !(path === '/cart' && !fromTab)) return;
      // The investor's Sales page (Business Rule #56) is their home screen —
      // reached with `replace`, so there is nothing behind it to swipe to.
      if (path === '/investor') return;
      startX = t.clientX; startY = t.clientY; armed = true;
    };

    const onMove = (e) => {
      if (!armed || fired) return;
      const t = e.touches[0];
      const dx = t.clientX - startX;
      const dy = t.clientY - startY;
      if (!decided) {
        if (Math.abs(dx) < SLOP_PX && Math.abs(dy) < SLOP_PX) return;
        // Must be rightward AND dominantly horizontal, else cancel (vertical scroll).
        if (dx <= 0 || Math.abs(dx) < Math.abs(dy) * 1.2) { armed = false; return; }
        decided = true;
      }
      if (dx > COMMIT_PX) {
        fired = true; armed = false;
        // Depth-0 guard: a cold-open deep link / push-notification landing has no
        // prior entry — go home rather than off the end of history.
        const idx = window.history.state?.idx;
        if (typeof idx === 'number' && idx <= 0) navigate('/');
        else navigate(-1);
      }
    };

    const onEnd = () => { armed = decided = fired = false; };

    const opts = { passive: true };
    document.addEventListener('touchstart', onStart, opts);
    document.addEventListener('touchmove', onMove, opts);
    document.addEventListener('touchend', onEnd, opts);
    document.addEventListener('touchcancel', onEnd, opts);
    return () => {
      document.removeEventListener('touchstart', onStart, opts);
      document.removeEventListener('touchmove', onMove, opts);
      document.removeEventListener('touchend', onEnd, opts);
      document.removeEventListener('touchcancel', onEnd, opts);
    };
  }, [navigate]);
}
